import { useCallback } from "react";
import { useRouter } from "next/router";
import { VOD_LIST_URL_QUERY_KEYS_ORDER } from "~/components/blocks/vod/list/const/search-facets";
import type { SearchVideoUrlQuery } from "~/components/blocks/vod/list/types";
import { CATEGORY_IDS } from "~/const/categories";
import type { ListSearchQuery, VodHrefMap } from "~/const/hrefs";
import { PATHS } from "~/const/paths";
import { removeObjectKey } from "~/util/remove-object-key";

type PushToVodListArgs = {
  query?: ListSearchQuery;
  isGravure?: boolean;
};

type ReplaceToVodListArgs = {
  query?: ListSearchQuery;
  scroll?: boolean;
  isGravure?: boolean;
};

type BuildVodListHrefArgs = {
  query: ListSearchQuery;
  isGravure?: boolean;
};

/**
 * 作品一覧(`/vod/list/`)へのページ遷移関連処理のフック
 *
 * SEO要件のためにURLクエリパラメータをソートするなどをここで行っている
 */
export const useRouterForVodList = () => {
  const { push, replace } = useRouter();

  const buildVodListHref = useCallback(
    (args: BuildVodListHrefArgs): VodHrefMap["list"] | VodHrefMap["gravure_list"] => {
      const { query, isGravure = false } = args;
      // 作品一覧において,categories="23"を含むURLの場合,遷移先を/vod/restrict/listに遷移させる
      const newPathname =
        isGravure || query.categories === CATEGORY_IDS.GRAVURE ? PATHS.VOD_GRAVURE_LIST : PATHS.VOD_LIST;
      // グラビア作品一覧において、categoriesをqueryから省く
      const newQuery =
        isGravure || query.categories === CATEGORY_IDS.GRAVURE ? { ...query, categories: undefined } : query;
      return {
        pathname: newPathname,
        query: sortListQueryParamsForSeo(newQuery),
      };
    },
    []
  );

  const pushToVodList = useCallback(
    (args: PushToVodListArgs = {}) => {
      const { query = {}, isGravure = false } = args;
      return push(buildVodListHref({ query, isGravure }));
    },
    [buildVodListHref]
  );

  const replaceToVodList = useCallback(
    (args: ReplaceToVodListArgs = {}) => {
      const { query = {}, scroll = true, isGravure = false } = args;
      return replace(buildVodListHref({ query, isGravure }), undefined, { scroll });
    },
    [buildVodListHref]
  );

  const pushToVodListWithoutProductModal = useCallback(
    (args: PushToVodListArgs = {}) => {
      const { query = {}, isGravure = false } = args;
      return pushToVodList({ query: filterProductModalQueryKeys(query), isGravure });
    },
    [pushToVodList]
  );

  const replaceToVodListWithoutProductModal = useCallback(
    (args: ReplaceToVodListArgs = {}) => {
      const { query = {}, isGravure = false } = args;
      return replaceToVodList({ query: filterProductModalQueryKeys(query), isGravure });
    },
    [replaceToVodList]
  );

  const filterProductModalQueryKeys = (query: ListSearchQuery): SearchVideoUrlQuery => {
    // eslint-disable-next-line unused-imports/no-unused-vars
    const { season: _season, content: _content, ...rest } = query;
    return rest;
  };

  return {
    pushToVodList,
    replaceToVodList,
    buildVodListHref,
    pushToVodListWithoutProductModal,
    replaceToVodListWithoutProductModal,
  };
};

const sortListQueryParamsForSeo = (urlQuery: ListSearchQuery): ListSearchQuery => {
  const sortedQueryParams = VOD_LIST_URL_QUERY_KEYS_ORDER.reduce((prev, key) => {
    if (urlQuery[key] === undefined) {
      return prev;
    }
    const conditions = urlQuery[key]?.split(",") ?? [];
    const uniqueConditions = Array.from(new Set(conditions));
    return { ...prev, [key]: uniqueConditions.sort().join(",") };
  }, {} as ListSearchQuery);

  const diffQueryParams = VOD_LIST_URL_QUERY_KEYS_ORDER.reduce((prev, key) => {
    return removeObjectKey(prev, key);
  }, urlQuery);

  return { ...sortedQueryParams, ...diffQueryParams };
};
